import React, {useEffect, useRef, useState} from 'react';
import {Platform, StyleSheet, Text, TouchableWithoutFeedback, View} from "react-native";
import {useDispatch, useSelector} from "react-redux";
import {Icon} from "react-native-elements";
import moment from "moment";
import momentTz from "moment-timezone";
import {formatTimeFromMoment, getDaySchedule, range} from "../services/helpers/dateTimeHelpers";
import _ from "lodash";
import {getBookingList, getBookingListExtended, getComponentColor, getConfig} from "../reducers/configSlice";
import {GestureHandlerRootView, ScrollView} from "react-native-gesture-handler";
import {FONT_FAMILY_BOLD, FONT_FAMILY_LIGHT} from "../constants/styleConstants";
import {actuatedWithoutScale} from "../services/helpers/stylingHelpers";
import {isWEB} from "../services/helpers/getPlatformType";
import Dash from "./Dash";

const Color = require('color');
const setAlpha = (color, alpha, grey = false) => {
    return grey ? Color(color).grayscale().alpha(alpha).rgb().string() : Color(color).alpha(alpha).rgb().string()
}

const convertToTime = (value, timeFormat) => {
    const hours = parseInt(value);
    return moment(hours, 'HH').format(timeFormat === "ampm" ? 'h A' : "HH")
};

const getCurrentTimePercentage = (tz, dayStart) => {
    return (((parseInt(momentTz.tz(tz).format("H")) - dayStart) * 60 + parseInt(momentTz.tz(tz).format("mm"))) / 60).toFixed(2);
}

const getCurrentTimeMarginTop = (tz, dayStart) => {
    const percentage = getCurrentTimePercentage(tz, dayStart)
    return percentage * actuatedWithoutScale(1.1 * 120) + parseInt(percentage) * actuatedWithoutScale(1.1 * 3) - actuatedWithoutScale(1.1 * 7);
}

const BookingsSchedule = () => {
    const scrollViewRef = useRef();
    const bookingListExtended = useSelector(getBookingListExtended)
    const config = useSelector(getConfig)
    const bookingList = useSelector(getBookingList)
    const componentColor = useSelector(getComponentColor)
    const dispatch = useDispatch()
    const [extended, setExtended] = useState(false)
    const [hours, setHours] = useState(range(parseInt(getDaySchedule().start), parseInt(getDaySchedule().end)))

    const debounce = _.debounce( () => {
        if (extended) {
            switchExtended(false)
        }

        scrollToCurrent();
    }, 60000);

    const scrollToCurrent = () => {
        setTimeout( () => {
            if (scrollViewRef && scrollViewRef.current) {
                scrollViewRef.current.scrollTo({
                    y: getCurrentTimeMarginTop(config.timezone, parseInt(getDaySchedule().start)) - (3.4 * actuatedWithoutScale(1.1 * 120)),
                    animated: true,
                });
            }
        }, 100)
    };

    useEffect( () => {
        scrollToCurrent()
    }, [])

    useEffect(() => {
        if (bookingListExtended) {
            switchExtended(true)
            debounce()
        } else {
            switchExtended(false)
            debounce.cancel()
        }
    }, [bookingListExtended])

    const interpolated = {
        scrollView: {
            marginRight: extended ? actuatedWithoutScale(1.1 * 25) : 0,
            overflow: 'visible',
            backgroundColor: extended ? setAlpha(componentColor, 0.25) : setAlpha(componentColor, 0),
        },
        hourContainer: {
            paddingLeft: extended ? actuatedWithoutScale(1.1 * 10) : actuatedWithoutScale(1.1 * 2)
        },
        hourWrapper: {
            borderLeftColor: setAlpha(componentColor, 0.6),
            borderRightColor: setAlpha(componentColor, 0.3),
            borderRightWidth: extended ? actuatedWithoutScale(1.1 * 1) : 0
        },
        hourDash: {
            borderTopColor: setAlpha(componentColor, 0.6)
        },
        hour: {
            borderTopColor: setAlpha(componentColor, 0.5, true),
            marginLeft: extended ? actuatedWithoutScale(1.1 * 3): 0,
            borderTopWidth: extended ? 1 : 0,
        },
        hourDashRight: {
            borderTopColor: setAlpha(componentColor, 0.6),
            opacity: extended? 1 : 0,
        },
        bookingMarginLeft: {
            marginLeft: extended ? actuatedWithoutScale(1.1 * 18) : actuatedWithoutScale(1.1 * 8)
        },
        bookingData: {
            backgroundColor: setAlpha(componentColor, 0.15),
            opacity : extended ? 1 : 0
        },
        bookingRight: {
            backgroundColor: setAlpha(componentColor, 0.6),
            opacity: extended ? 1 : 0
        },
        currentBooking: {
            marginLeft: extended ? actuatedWithoutScale(1.1 * 10) : actuatedWithoutScale(1.1 * 8)
        },
        currentBookingHighlighter: {
            opacity: extended ? 1 : 0
        },
    }

    const renderHour = (item, key) => {
        return (
            <View key={key} style={[styles.hourContainer, interpolated.hourContainer]}>
                <View style={[styles.hourWrapper, interpolated.hourWrapper]}>
                    <View style={[styles.hourDash, interpolated.hourDash]}/>
                    <View style={[styles.hour, interpolated.hour]}>
                        <Text style={styles.hourText}>{convertToTime(item, config.time_format)}</Text>
                    </View>
                    <View style={[styles.hourDash, styles.hourDashRight, interpolated.hourDashRight]}/>
                </View>
            </View>
        );
    };

    const renderBooking = (booking, key) => {
        const bookingStartMoment = moment(booking.start, 'YYYY-MM-DD HH:mm')
        const bookingEndMoment = moment(booking.end, 'YYYY-MM-DD HH:mm')
        const bookingEndMomentHours = (bookingEndMoment.hours() === 0 && bookingEndMoment.minutes() === 0 ? 24 : bookingEndMoment.hours());

        let startPercentage = (((parseInt(bookingStartMoment.hours()) - parseInt(getDaySchedule().start)) * 60 + parseInt(bookingStartMoment.minutes())) / 60).toFixed(2);
        let endPercentage = (((parseInt(bookingEndMomentHours) - parseInt(getDaySchedule().start)) * 60 + parseInt(bookingEndMoment.minutes())) / 60).toFixed(2);

        if (parseFloat(endPercentage) === 0.00) {
            endPercentage = 24.00;
        }

        let bookingLength = parseFloat(endPercentage) - parseFloat(startPercentage);
        let marginTop = startPercentage * actuatedWithoutScale(1.1 * 120) + parseInt(startPercentage) * actuatedWithoutScale(1.1 * 3);
        let height = bookingLength * actuatedWithoutScale(1.1 * 120);


        if (parseInt(endPercentage - startPercentage) > 1) {
            height = height + parseInt(endPercentage - startPercentage) * actuatedWithoutScale(1.1 * 3);
        }

        if (booking.current) {
            let durationPassed = (((parseInt(momentTz.tz(config.timezone).format("H")) * 60 + parseInt(momentTz.tz(config.timezone).format("mm"))) - startPercentage) / 60).toFixed(2);
            let durationLeft = Number.parseFloat(bookingLength - durationPassed).toFixed(2);

            return (
                    <View key={booking.booking_id} style={[styles.currentBooking, {height: height, marginTop: marginTop, width: extended ? '100%' : '1%'}, interpolated.currentBooking]}>
                        <View style={[styles.currentBookingHighlighter, {backgroundColor: setAlpha(componentColor, 0.4), height: '105%'}, interpolated.currentBookingHighlighter]} />

                        <View style={[styles.currentBookingLeft, {height: height, marginTop: marginTop, backgroundColor: setAlpha(componentColor, 0.6)}, extended && styles.currentBookingLeftExtended, {backgroundColor: setAlpha(componentColor, 0.6)}]}>
                            <View style={[styles.currentBookingRemaining, {backgroundColor: setAlpha(componentColor, 1), marginTop: marginTop, height: durationLeft * actuatedWithoutScale(1.1 * 120) + parseInt(durationLeft) * actuatedWithoutScale(1.1 * 3)}]} />
                        </View>
                        <View style={[styles.currentBookingData, {height: height >= actuatedWithoutScale(1.1 * 20) ? height : height + actuatedWithoutScale(1.1 * 5), marginTop: height >= actuatedWithoutScale(1.1 * 35) ? actuatedWithoutScale(1.1 * 10) : height >= actuatedWithoutScale(1.1 * 30) ? actuatedWithoutScale(1.1 * 6) : height >= actuatedWithoutScale(1.1 * 20) ? actuatedWithoutScale(1.1 * 2) : -actuatedWithoutScale(1.1 * 2)}, interpolated.bookingData, {backgroundColor: null}]}>
                            {
                                height > actuatedWithoutScale(1.1 * 30) ?
                                    <Text style={styles.currentBookingInterval}>
                                        {formatTimeFromMoment(bookingStartMoment)} - {formatTimeFromMoment(bookingEndMoment)}
                                    </Text> : null
                            }
                            {
                                !config.hide_booking_title ?
                                    <Text numberOfLines={height >= actuatedWithoutScale(1.1 * 90) ? actuatedWithoutScale(1.1 * 3) : height >= actuatedWithoutScale(1.1 * 60) ? actuatedWithoutScale(1.1 * 2) : actuatedWithoutScale(1.1)} ellipsizeMode="tail" style={[styles.currentBookingTitle, {fontSize: height >= actuatedWithoutScale(1.1 * 35) ? actuatedWithoutScale(1.1 * 20) : height >= actuatedWithoutScale(1.1 * 30) ? actuatedWithoutScale(1.1 * 18) : height >= actuatedWithoutScale(1.1 * 20) ? actuatedWithoutScale(1.1 * 18) : actuatedWithoutScale(1.1 * 14)}]}>
                                        {booking.title}
                                    </Text> : null
                            }
                        </View>
                        <View style={[styles.currentBookingRight, {height: height}, interpolated.bookingRight]} />
                    </View>

            );
        } else {
            return (
                <View key={booking.booking_id} style={[ styles.booking, interpolated.bookingMarginLeft]}>
                    <View style={[styles.bookingLeft, {height: height, marginTop: marginTop, backgroundColor: setAlpha(componentColor, 0.6)}]} />
                    <View style={[styles.bookingData, {height: height, marginTop: marginTop }, interpolated.bookingData]}>
                        {
                            height >= actuatedWithoutScale(1.1 * 30) ?
                                <Text style={styles.bookingInterval}>
                                    {formatTimeFromMoment(bookingStartMoment)} - {formatTimeFromMoment(bookingEndMoment)}
                                </Text> : null
                        }
                        {
                            (height >= actuatedWithoutScale(1.1 * 30)) && !config.hide_booking_title ?
                                <Text numberOfLines={height >= actuatedWithoutScale(1.1 * 90) ? actuatedWithoutScale(1.1 * 3) : height >= actuatedWithoutScale(1.1 * 60) ? actuatedWithoutScale(1.1 * 2) : actuatedWithoutScale(1.1)} ellipsizeMode="tail" style={[styles.bookingTitle, {fontSize: height >= actuatedWithoutScale(1.1 * 18) ? actuatedWithoutScale(1.1 * 15) : actuatedWithoutScale(1.1 * 11)}]}>
                                    {booking.title}
                                </Text> : null
                        }
                    </View>
                    <View style={[styles.bookingRight, {height: height, marginTop: marginTop}, interpolated.bookingRight]} />
                </View>
            )
        }
    };

    const switchExtended = (value) => {
        setExtended(value)
    }

    return (
        config.hide_schedule ? null :
            <View style={[{flex: extended ? 0.6 : 0.1}, isWEB() && { overflow: 'scroll' }]}>
                <GestureHandlerRootView style={{ flex: 1 }}>
                    <ScrollView
                        bounces={false}
                        showsVerticalScrollIndicator={false}
                        style={interpolated.scrollView}
                        ref={scrollViewRef}
                        onScroll={(e) => {
                            debounce.cancel()
                        }}
                        scrollEventThrottle={400}
                        onMomentumScrollEnd={(e) => {debounce()}}
                        onScrollEndDrag={(e) => {debounce()}}
                        keyboardShouldPersistTaps='always'
                    >
                        <TouchableWithoutFeedback onPress={() => {switchExtended(!extended)}} waitFor={scrollViewRef}>

                            <View>
                                {hours.map(renderHour)}
                                {bookingList.map(renderBooking)}
                                {extended ?
                                    <View style={[styles.currentTimeIndicatorExtended, {marginTop: getCurrentTimeMarginTop(config.timezone, parseInt(getDaySchedule().start)) + actuatedWithoutScale(1.1 * 5)}]}>
                                        <Icon size={actuatedWithoutScale(10)} name="caret-left" type="font-awesome" color="#27D6F9"/>
                                        <Dash style={styles.dash} dashGap={3} dashColor="#1da8c4" dashThickness={0.5}/>
                                        <Icon size={actuatedWithoutScale(10)} name="caret-left" type="font-awesome" color="#27D6F9"/>
                                    </View> :
                                    <Icon size={actuatedWithoutScale(20)} name="caret-left" type="font-awesome" color="#27D6F9" containerStyle={[styles.currentTimeIndicator, {top: getCurrentTimeMarginTop(config.timezone, parseInt(getDaySchedule().start)) }]}/>
                                }
                            </View>
                        </TouchableWithoutFeedback>
                    </ScrollView>
                </GestureHandlerRootView>
            </View>
    );
}


const styles = StyleSheet.create({
    bookingList: {
        flex: 0.10,
    },
    currentTimeIndicator: {
        position: 'absolute',
        marginLeft: actuatedWithoutScale(1.1 * 14)
    },
    currentTimeIndicatorExtended: {
        position: 'absolute',
        marginLeft: actuatedWithoutScale(1.1 * 16),
        flexDirection: 'row'
    },
    dash: {
        width: '100%',
        height: actuatedWithoutScale(1.1),
        marginLeft: actuatedWithoutScale(1.1 * 2),
        marginTop: actuatedWithoutScale(1.1 * 5)
    },
    hourContainer: {
        height: actuatedWithoutScale(1.1 * 120),
        marginBottom: actuatedWithoutScale(1.1 * 3)
    },
    hourWrapper: {
        borderLeftWidth: actuatedWithoutScale(1.1 * 3),
        flex: 1,
        flexDirection: 'row',
    },
    hourDash: {
        width: actuatedWithoutScale(1.1 * 6),
        borderTopWidth: 1,
        marginLeft: actuatedWithoutScale(1.1 * 7)
    },
    hourDashRight: {
        marginLeft: 'auto',
        marginRight: actuatedWithoutScale(1.1 * 12)
    },
    hour: {
        alignItems: 'center',
        width: actuatedWithoutScale(1.1 * 50),
    },
    hourText: {
        color: '#7E888F',
        marginTop: actuatedWithoutScale(1.1 * 8),
        fontFamily: FONT_FAMILY_LIGHT,
        fontSize: actuatedWithoutScale(1.1 * 14)
    },
    currentBooking: {
        position: 'absolute',
        marginLeft: actuatedWithoutScale(1.1 * 8),
        width: '1%'
    },
    currentBookingLeft: {
        position: 'absolute',
        bottom: 0,
        width: actuatedWithoutScale(1.1 * 3),
        borderRadius: actuatedWithoutScale(1.1 * 10),
    },
    currentBookingExtended: {
        position: 'absolute',
        marginLeft: actuatedWithoutScale(1.1 * 10),
        backgroundColor: 'rgba(0,0,0,0)',
        width: '100%',
    },
    currentBookingHighlighter: {
        position: 'absolute',
        width: '105%',
        borderRadius: 8,
        opacity: 0,
        marginLeft: -actuatedWithoutScale(1.1 * 10),
        marginTop: -actuatedWithoutScale(1.1 * 5),
        marginBottom: -actuatedWithoutScale(1.1 * 5),
    },
    currentBookingLeftExtended: {
        position: 'absolute',
        borderRadius: actuatedWithoutScale(1.1 * 10)
    },
    currentBookingRemaining: {
        position: 'absolute',
        bottom: 0,
        width: actuatedWithoutScale(1.1 * 2),
        shadowOffset:{  width: 0,  height: 0 },
        shadowColor: 'white',
        shadowOpacity: 1.0,
        borderRadius: 10
    },
    currentBookingData: {
        position: 'absolute',
        width: '68%',
        left: '25%',
        opacity: 0
    },
    currentBookingInterval: {
        fontFamily: FONT_FAMILY_LIGHT,
        color: '#B2D4DA',
        fontSize: actuatedWithoutScale(1.1 * 14)
    },
    currentBookingTitle: {
        fontFamily: FONT_FAMILY_BOLD,
        color: '#FAFBFB',
    },
    currentBookingRight: {
        width: actuatedWithoutScale(1.1 * 2),
        position: 'absolute',
        right: actuatedWithoutScale(1.1 * 15),
        borderRadius: actuatedWithoutScale(1.1 * 10),
    },
    booking: {
        width: '100%',
        position: 'absolute',
        marginLeft: actuatedWithoutScale(1.1 * 8)
    },
    bookingExtended: {
        width: '100%',
        position: 'absolute',
        marginLeft: actuatedWithoutScale(1.1 * 16)
    },
    bookingLeft: {
        width: actuatedWithoutScale(1.1),
        position: 'absolute',
    },
    bookingData: {
        position: 'absolute',
        width: '93%',
        paddingLeft: actuatedWithoutScale(1.1 * 70),
        opacity: 0,
    },
    bookingInterval: {
        fontFamily: FONT_FAMILY_LIGHT,
        color: '#9CB1B7',
        fontSize: actuatedWithoutScale(1.1 * 12)
    },
    bookingTitle: {
        fontFamily: FONT_FAMILY_BOLD,
        color: '#FAFBFB',
    },
    bookingRight: {
        width: 1,
        position: 'absolute',
        right: actuatedWithoutScale(1.1 * 24)
    }
});

export default BookingsSchedule;
